import { decrypt } from "../utilities/helpers";
import { ROLES } from "./roles";
import { normalizeRoles } from "./permissions";

// Username-level restrictions.
//
// The role matrix in `permissions.js` cannot express these: the Setup Admin
// account is mapped to the Admin role in AD, so any role-based check would let
// it through. The block is therefore keyed on the username and takes priority
// over whatever roles the account carries.
//
// Keys must be lowercase; matching is case-insensitive. Values are top-level
// route paths (as declared in `routes.js`) — a blocked path also blocks every
// route nested under it.
export const RESTRICTED_USERS = {
  setupadmin: [
    "assetOnboarding",
    "scanManagement",
    "configuration/administration/Schedulers",
    "configuration/administration/loginPageConfiguration",
    "configuration/auditLog",
    "configuration/usersession",
  ],
};

// Where an account lands when it signs in without asking for a specific page.
export const DEFAULT_LANDING_PATH = "/assetOnboarding/onboard";

// Accounts whose landing page differs from the default. Setup Admin is blocked
// from Asset Onboarding, so sending it to the default would drop it straight on
// the access-denied page.
export const LANDING_PATHS = {
  setupadmin: "/configuration/administration/tools",
};

// The same idea a role at a time. Supervisor cannot see Asset Onboarding, so
// the default landing would be an access-denied page for it; Approval is the
// only page it has, and therefore its home.
export const ROLE_LANDING_PATHS = {
  [ROLES.SUPERVISOR]: "/assetOnboarding/approval",
};

// `state.common.loggedUser.userName` holds the encrypted username. Fall back to
// the raw value so a plain username still matches, and lowercase so the
// comparison is case-insensitive.
export const resolveUserName = (userName) => {
  if (typeof userName !== "string" || !userName.trim()) {
    return "";
  }
  let decrypted = null;
  try {
    decrypted = decrypt(userName);
  } catch {
    decrypted = null;
  }
  return (decrypted || userName).trim().toLowerCase();
};

// Whether this account is the Setup Admin username.
export const isSetupAdminUser = (userName) =>
  resolveUserName(userName) === "setupadmin";

// Top-level paths this account may never reach. Unknown users are unrestricted.
export const getBlockedPaths = (userName) =>
  RESTRICTED_USERS[resolveUserName(userName)] || [];

// Whether this account is blocked from a location. Accepts both route paths
// (`configuration/auditLog`) and router pathnames (`/configuration/auditLog`).
export const isPathBlockedForUser = (userName, pathName) => {
  if (typeof pathName !== "string" || !pathName) {
    return false;
  }
  const path = pathName.replace(/^\/+/, "");
  return getBlockedPaths(userName).some(
    (blocked) => path === blocked || path.startsWith(`${blocked}/`),
  );
};

// Whether this account is blocked from a route. Routes without a path (or an
// unrestricted account) are never blocked, so this composes with the role
// checks rather than replacing them.
export const isRouteBlockedForUser = (userName, route) =>
  isPathBlockedForUser(userName, route?.path);

// The page to open for this account when no specific destination was asked
// for. The account's own landing wins over its role's — a username restriction
// is the more specific of the two. `userRole` is optional: sign-in resolves
// this before the role is known, and PrivateRoute resolves it again with the
// role once it has come back.
export const getLandingPath = (userName, userRole) => {
  const byUser = LANDING_PATHS[resolveUserName(userName)];
  if (byUser) {
    return byUser;
  }
  const byRole = normalizeRoles(userRole)
    .map((role) => ROLE_LANDING_PATHS[role])
    .find(Boolean);
  return byRole || DEFAULT_LANDING_PATH;
};

// Where to send someone who cannot see the page they have been handed.
//
// Sign-in has to choose a landing page before the role has come back, so it
// can only guess the default. A role with a landing page of its own is moved
// there rather than being dropped on the access-denied page for a destination
// it never asked for. Any other forbidden page was a deliberate visit, and
// access-denied is the honest answer to that.
export const resolveDeniedPath = (userName, userRole, pathName) => {
  const landing = getLandingPath(userName, userRole);
  const path = `/${String(pathName || "").replace(/^\/+/, "")}`;
  if (path === DEFAULT_LANDING_PATH && landing !== DEFAULT_LANDING_PATH) {
    return landing;
  }
  return "/access-denied";
};

// The page to open after sign-in. A remembered destination wins, unless this
// account is blocked from it — then the landing page is used instead.
export const resolveInitialPath = (userName, requestedPath, userRole) => {
  if (requestedPath && !isPathBlockedForUser(userName, requestedPath)) {
    return requestedPath;
  }
  return getLandingPath(userName, userRole);
};
